import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Save } from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { portalGet, portalPost, hu, apiError } from '../../lib/portalApi';

const CIRC = [
  ['neck_cm', 'Nyak'], ['chest_cm', 'Mellkas'], ['waist_cm', 'Derék (köldöknél)'],
  ['hip_cm', 'Csípő'], ['arm_cm', 'Felkar (feszítve)'], ['thigh_cm', 'Comb (középen)'],
];
const SKINFOLDS = [
  ['sf_chest', 'Mell'], ['sf_axilla', 'Hónalj'], ['sf_triceps', 'Tricepsz'],
  ['sf_subscap', 'Lapocka alatt'], ['sf_abdomen', 'Has'], ['sf_suprail', 'Csípő felett'],
  ['sf_thigh', 'Comb'],
];

// Jackson–Pollock 7 pont → testsűrűség → Siri-képlet
const jp7 = (sum, age, gender) => {
  if (!sum || !age) return null;
  const d = gender === 'female'
    ? 1.097 - 0.00046971 * sum + 0.00000056 * sum * sum - 0.00012828 * age
    : 1.112 - 0.00043499 * sum + 0.00000055 * sum * sum - 0.00028826 * age;
  return Math.round(((495 / d) - 450) * 10) / 10;
};

export const BodyFatTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [age, setAge] = useState(28);
  const [gender, setGender] = useState('male');
  const [f, setF] = useState({ weight_kg: '' });

  const load = () => portalGet('/training')
    .then(d => setRows(d.measurements || []))
    .catch(() => {})
    .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const sumSf = useMemo(
    () => SKINFOLDS.reduce((s, [k]) => s + (Number(f[k]) || 0), 0),
    [f],
  );
  const allSf = SKINFOLDS.every(([k]) => Number(f[k]) > 0);
  const bf = allSf ? jp7(sumSf, Number(age), gender) : null;

  const save = async () => {
    const payload = Object.fromEntries(
      Object.entries(f).filter(([, v]) => v !== '' && v !== null).map(([k, v]) => [k, Number(v)]),
    );
    if (Object.keys(payload).length === 0) { toast.error('Adj meg legalább egy értéket.'); return; }
    if (bf) payload.body_fat_pct = bf;
    setBusy(true);
    try {
      await portalPost('/measurements', payload);
      toast.success('Mérés mentve');
      setF({ weight_kg: '' });
      await load();
    } catch (err) {
      toast.error(apiError(err, 'A mentés nem sikerült.'));
    } finally { setBusy(false); }
  };

  const chart = rows
    .filter(r => r.body_fat_pct || r.waist_cm || r.weight_kg)
    .map(r => ({
      d: (r.created_at || '').slice(5, 10),
      bf: r.body_fat_pct || null,
      derek: r.waist_cm || null,
      suly: r.weight_kg || null,
    }));

  return (
    <div data-testid="bodyfat-tab" className="space-y-6">
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h3 className="text-[11px] font-black uppercase tracking-[0.14em] text-gray-500 mb-4">Új mérés</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-gray-500">Testsúly (kg)</Label>
              <Input data-testid="bf-weight" type="number" value={f.weight_kg}
                onChange={e => set('weight_kg', e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-gray-500">Életkor</Label>
              <Input data-testid="bf-age" type="number" value={age} onChange={e => setAge(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-gray-500">Nem</Label>
              <select data-testid="bf-gender" value={gender} onChange={e => setGender(e.target.value)}
                className="mt-1 w-full h-10 rounded-md border border-gray-300 px-3 text-sm bg-white">
                <option value="male">Férfi</option>
                <option value="female">Nő</option>
              </select>
            </div>
          </div>

          <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mt-5 mb-2">Körméretek (cm)</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {CIRC.map(([k, t]) => (
              <div key={k}>
                <Label className="text-[11px] text-gray-500">{t}</Label>
                <Input data-testid={`bf-${k}`} type="number" value={f[k] || ''}
                  onChange={e => set(k, e.target.value)} className="mt-1" />
              </div>
            ))}
          </div>

          <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mt-5 mb-2">Bőrredők kaliperrel (mm)</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {SKINFOLDS.map(([k, t]) => (
              <div key={k}>
                <Label className="text-[11px] text-gray-500">{t}</Label>
                <Input data-testid={`bf-${k}`} type="number" min="1" max="80" value={f[k] || ''}
                  onChange={e => set(k, e.target.value)} className="mt-1" />
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-3">
            Nem kötelező. Ha mind a 7 pontot kitöltöd, a testzsírt a Jackson–Pollock képlet is kiszámolja.
          </p>

          <Button data-testid="bf-save" disabled={busy} onClick={save}
            className="mt-4 bg-gray-900 hover:bg-gray-800 text-white font-bold">
            {busy ? <Loader2 size={15} className="animate-spin mr-1.5" /> : <Save size={15} className="mr-1.5" />}
            Mérés mentése
          </Button>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h3 className="text-[11px] font-black uppercase tracking-[0.14em] text-gray-500 mb-4">Aktuális számítás</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg bg-gray-50 p-3">
              <div className="text-[10px] uppercase tracking-wider text-gray-500">Bőrredő-összeg</div>
              <div className="text-2xl font-black text-gray-900" data-testid="bf-sum">{hu(sumSf)} <span className="text-sm">mm</span></div>
            </div>
            <div className="rounded-lg bg-emerald-50 p-3">
              <div className="text-[10px] uppercase tracking-wider text-gray-500">Testzsír (kaliper)</div>
              <div className="text-2xl font-black text-emerald-700" data-testid="bf-percent">
                {bf ? `${hu(bf, 1)}%` : '—'}
              </div>
            </div>
            {f.weight_kg && bf && (
              <>
                <div className="rounded-lg bg-gray-50 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-gray-500">Zsírmentes tömeg</div>
                  <div className="text-xl font-black text-gray-900">
                    {hu(Number(f.weight_kg) * (1 - bf / 100), 1)} kg
                  </div>
                </div>
                <div className="rounded-lg bg-gray-50 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-gray-500">Zsírtömeg</div>
                  <div className="text-xl font-black text-gray-900">
                    {hu(Number(f.weight_kg) * (bf / 100), 1)} kg
                  </div>
                </div>
              </>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-4 leading-relaxed">
            A kaliperes érték a legmegbízhatóbb olcsó mutató. A testzsír %-ot a „Makró &amp; naptípus"
            fülön beírva a kalkulátor a zsírmentes tömegből számol (Katch–McArdle).
          </p>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="text-[11px] font-black uppercase tracking-[0.14em] text-gray-500 mb-4">Előzmények</h3>
        {loading ? (
          <div className="text-sm text-gray-500 flex items-center gap-2">
            <Loader2 size={15} className="animate-spin" /> Betöltés…
          </div>
        ) : chart.length === 0 ? (
          <p className="text-sm text-gray-500">Még nincs mentett mérésed.</p>
        ) : (
          <div className="h-64" data-testid="bf-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chart} margin={{ top: 5, right: 10, bottom: 0, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="d" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} domain={[0, 40]} />
                <Tooltip />
                <Line yAxisId="right" type="monotone" dataKey="bf" name="Testzsír %" stroke="#dc2626" strokeWidth={2} dot />
                <Line yAxisId="left" type="monotone" dataKey="derek" name="Derék (cm)" stroke="#111827" strokeWidth={2} dot />
                <Line yAxisId="left" type="monotone" dataKey="suly" name="Testsúly (kg)" stroke="#2563eb" strokeWidth={2} dot />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

export default BodyFatTab;
